import { PaletteMode } from "@mui/material";
import Styles from "./styles";
import { UserPaletteOverrides } from "../types/userSettings";

export type ThemePaletteOverridesByMode = {
  light?: UserPaletteOverrides;
  dark?: UserPaletteOverrides;
};

const normalizeMuiColor = (value?: string): string | undefined => {
  if (!value) return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const withOptionalHexPrefix =
    /^[\da-fA-F]{3}$/.test(trimmed) || /^[\da-fA-F]{6}$/.test(trimmed)
      ? `#${trimmed}`
      : trimmed;

  const isSupportedMuiColor =
    /^#[\da-fA-F]{3}$/.test(withOptionalHexPrefix) ||
    /^#[\da-fA-F]{6}$/.test(withOptionalHexPrefix) ||
    /^rgba?\(.+\)$/.test(withOptionalHexPrefix) ||
    /^hsla?\(.+\)$/.test(withOptionalHexPrefix) ||
    /^color\(.+\)$/.test(withOptionalHexPrefix);

  return isSupportedMuiColor ? withOptionalHexPrefix : undefined;
};

const applyOverrides = (
  base: any,
  overrides?: UserPaletteOverrides
): any => {
  if (!overrides) return base;

  const primary = normalizeMuiColor(overrides.primary);
  const secondary = normalizeMuiColor(overrides.secondary);
  const warning = normalizeMuiColor(overrides.warning);
  const error = normalizeMuiColor(overrides.error);
  const success = normalizeMuiColor(overrides.success);
  const info = normalizeMuiColor(overrides.info);

  return {
    ...base,
    primary: primary ? { ...base.primary, main: primary } : base.primary,
    secondary: secondary ? { ...base.secondary, main: secondary } : base.secondary,
    warning: warning ? { ...base.warning, main: warning } : base.warning,
    error: error ? { ...base.error, main: error } : base.error,
    success: success ? { ...base.success, main: success } : base.success,
    info: info ? { ...base.info, main: info } : base.info,
  };
};

const ThemeOptions = (
  mode: PaletteMode,
  overridesByMode?: ThemePaletteOverridesByMode
) => {
  const base = mode === "light" ? Styles.light : Styles.dark;
  const overrides =
    (mode === "light" ? overridesByMode?.light : overridesByMode?.dark) ??
    undefined;

  return {
    palette: {
      mode,
      ...applyOverrides(base, overrides),
    },
    typography: {
      button: {
        textTransform: "none" as const,
      },
    },
    components: {
      // Disable MUI's elevation-based translucent overlay on Paper in dark
      // mode, which adds a background-image gradient that lightens the
      // surface beyond palette.background.paper.
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
    },
  };
};

export default ThemeOptions;
